import React, { useState, useEffect } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  MdDownload,
  MdRefresh,
  MdCloudUpload,
  MdBed
} from "react-icons/md";
import camaApi from "../../../services/api/camaApi";
import * as XLSX from "xlsx";
import "react-toastify/dist/ReactToastify.css";
import "../../../components/css/Dashboard.css";

// Log to verify file load
console.log("CargaMasivaCamas.js loaded");

const CargaMasivaCamas = () => {
  const navigate = useNavigate();
  // Safe localStorage parsing
  const [selectedProject, setSelectedProject] = useState(null);

  useEffect(() => {
    console.log("CargaMasivaCamas mounted");
    try {
      const storedProject = localStorage.getItem("selectedProject");
      if (storedProject && storedProject !== "undefined") {
        const parsed = JSON.parse(storedProject);
        console.log("Project loaded:", parsed);
        setSelectedProject(parsed);
      }
    } catch (error) {
      console.error("Error loading selectedProject from localStorage:", error);
      toast.error("Error al cargar la información del proyecto");
    }
  }, []);

  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(false);
  const [results, setResults] = useState(null);
  const [fileValidationErrors, setFileValidationErrors] = useState([]);
  const [isFileValid, setIsFileValid] = useState(false);
  const [hasBeenProcessed, setHasBeenProcessed] = useState(false); // Nuevo: controla si ya se procesó

  const showToast = (type, message) => {
    if (type === "success") {
      toast.success(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "error") {
      toast.error(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "info") {
      toast.info(message, { position: "bottom-right", autoClose: 3000 });
    } else if (type === "warning") {
      toast.warning(message, { position: "bottom-right", autoClose: 3000 });
    }
  };

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
      if (!['xlsx', 'xls'].includes(fileExtension)) {
        showToast("error", 'Por favor seleccione un archivo Excel válido (.xlsx o .xls)');
        e.target.value = '';
        return;
      }

      // Validar archivo en el backend
      setIsValidating(true);
      setFileValidationErrors([]);
      setIsFileValid(false);
      setResults(null);
      setHasBeenProcessed(false); // Resetear el estado de procesado

      try {
        const validationResponse = await camaApi.validateBulkUploadFile(
          selectedFile,
          selectedProject?.id
        );

        if (validationResponse.data) {
          const validationData = validationResponse.data;

          if (validationData.success && validationData.total_errores === 0) {
            setIsFileValid(true);
            setHasBeenProcessed(false); // Asegurar que está listo para procesar
            setFile(selectedFile);
            showToast("success", `Archivo válido. ${validationData.filas_validas} filas listas para procesar.`);
          } else {
            setIsFileValid(false);
            setFileValidationErrors(validationData.errores || []);
            showToast("warning", `Se encontraron ${validationData.total_errores || 0} errores en el archivo. Revise los detalles.`);
          }
        }
      } catch (error) {
        console.error('Error al validar archivo:', error);
        setIsFileValid(false);
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          if (errorData.errores && Array.isArray(errorData.errores)) {
            setFileValidationErrors(errorData.errores);
          } else {
            showToast("error", errorData.detail || errorData.message || "Error al validar el archivo");
          }
        } else {
          showToast("error", "Error de conexión. Verifica tu conexión a internet.");
        }
      } finally {
        setIsValidating(false);
      }
    }
  };

  const handleProcessUpload = async () => {
    if (!file || !isFileValid) {
      showToast("warning", "Por favor, primero valide un archivo Excel válido.");
      return;
    }

    if (!selectedProject?.id) {
      showToast("error", "Debe seleccionar un proyecto.");
      return;
    }

    setIsLoading(true);
    setResults(null);

    try {
      const response = await camaApi.bulkUpload(
        file,
        selectedProject.id
      );

      if (response.data) {
        const resultados = response.data;

        if (resultados.success) {
          setResults({
            camas_creadas: resultados.camas_creadas || 0,
            camas_ya_existentes: resultados.camas_ya_existentes || 0,
            camas_fallidas: resultados.camas_fallidas || 0,
            habitaciones_creadas: resultados.habitaciones_creadas || 0,
            habitaciones_reutilizadas: resultados.habitaciones_reutilizadas || 0,
            modulos_creados: resultados.modulos_creados || 0,
            modulos_reutilizados: resultados.modulos_reutilizados || 0,
            zonas_creadas: resultados.zonas_creadas || 0,
            zonas_reutilizadas: resultados.zonas_reutilizadas || 0,
            detalle_camas_creadas: resultados.detalle_camas_creadas || [],
            detalle_camas_ya_existentes: resultados.detalle_camas_ya_existentes || [],
            detalle_camas_fallidas: resultados.detalle_camas_fallidas || [],
            detalle_habitaciones_creadas: resultados.detalle_habitaciones_creadas || [],
            detalle_habitaciones_reutilizadas: resultados.detalle_habitaciones_reutilizadas || [],
            detalle_modulos_creados: resultados.detalle_modulos_creados || [],
            detalle_modulos_reutilizados: resultados.detalle_modulos_reutilizados || [],
            detalle_zonas_creadas: resultados.detalle_zonas_creadas || [],
            detalle_zonas_reutilizadas: resultados.detalle_zonas_reutilizadas || []
          });
          
          setHasBeenProcessed(true); // Marcar como procesado

          showToast("success", `Procesamiento completado: ${resultados.camas_creadas} camas creadas, ${resultados.camas_ya_existentes || 0} ya existentes, ${resultados.camas_fallidas} fallidas.`);
        } else {
          showToast("error", resultados.message || "Error al procesar la carga masiva");
        }
      }
    } catch (error) {
      console.error('Error en procesamiento:', error);

      if (error.response && error.response.data) {
        const errorData = error.response.data;
        showToast("error", errorData.detail || errorData.message || "Error al procesar la carga masiva");
      } else {
        showToast("error", 'Error de conexión. Verifica tu conexión a internet.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = () => {
    // Crear template con las columnas requeridas: ZONA, MODULO, HABITACION, TIPO, CAMA
    const templateData = [
      {
        'ZONA': 'Zona A',
        'MODULO': 'M001',
        'HABITACION': '101',
        'TIPO': 'DOBLE',
        'CAMA': '1'
      },
      {
        'ZONA': 'Zona A',
        'MODULO': 'M001',
        'HABITACION': '101',
        'TIPO': 'DOBLE',
        'CAMA': '2'
      },
      {
        'ZONA': 'Zona A',
        'MODULO': 'M001',
        'HABITACION': '102',
        'TIPO': 'TRIPLE',
        'CAMA': '1'
      },
      {
        'ZONA': 'Zona A',
        'MODULO': 'M001',
        'HABITACION': '103',
        'TIPO': 'CAMAROTE',
        'CAMA': '1'
      },
      {
        'ZONA': 'Zona A',
        'MODULO': 'M001',
        'HABITACION': '103',
        'TIPO': 'CAMAROTE',
        'CAMA': '2'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "template_carga_masiva_camas.xlsx");

    showToast("success", "Template descargado exitosamente");
  };

  const resetForm = () => {
    setFile(null);
    setResults(null);
    setIsFileValid(false);
    setFileValidationErrors([]);
    setHasBeenProcessed(false); // Resetear estado de procesado
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
    showToast("info", "Formulario reiniciado");
  };

  if (selectedProject === null) {
    // Show loading or fallback while checking localStorage
    // Note: we start with null. If localStorage has value, it updates quickly.
    // If it stays null (e.g. really no project), we might want to show a warning or redirect.
    // But for now, let's render the layout but maybe with a spinner or just render safely.
    // To avoid "flash", we can return a spinner or empty (but user said "empty" was the problem).
    // Let's render the UI with "Cargando..." or just default to render with null project handled in UI.
    // Actually, let's just render. The UI handles selectedProject?.nombre fine.
  }

  return (
    <>
      <style>
        {`
          .dh-carga-bg {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            min-height: 100vh;
          }
          
          .dh-card-main {
            border: none;
            border-radius: 20px;
            box-shadow: 0 15px 35px rgba(0,0,0,0.1);
            background: white;
          }
          
          .dh-card-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            border-radius: 20px 20px 0 0;
            padding: 1.5rem 2rem;
            border: none;
          }
          
          .dh-header-title {
            color: white;
            font-weight: 700;
            font-size: 1.5rem;
            margin: 0;
          }
          
          .btn-dh-primary {
            background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(17, 153, 142, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-primary:hover {
            background: linear-gradient(135deg, #38ef7d 0%, #2dd4bf 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(17, 153, 142, 0.4);
            color: white;
          }
          
          .btn-dh-info {
            background: linear-gradient(135deg, #00c6ff 0%, #0072ff 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(0, 198, 255, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-info:hover {
            background: linear-gradient(135deg, #0072ff 0%, #0056cc 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(0, 198, 255, 0.4);
            color: white;
          }
          
          .btn-dh-secondary {
            background: linear-gradient(135deg, #6b7280 0%, #374151 100%);
            border: none;
            color: white;
            border-radius: 12px;
            padding: 10px 16px;
            font-weight: 600;
            box-shadow: 0 4px 14px rgba(107, 114, 128, 0.3);
            transition: all 0.3s ease;
          }
          
          .btn-dh-secondary:hover {
            background: linear-gradient(135deg, #374151 0%, #1f2937 100%);
            transform: translateY(-2px);
            box-shadow: 0 8px 25px rgba(107, 114, 128, 0.4);
            color: white;
          }
          
          .dh-form-control {
            border: 2px solid #e5e7eb;
            border-radius: 10px;
            padding: 12px 16px;
            font-size: 0.95rem;
            transition: all 0.3s ease;
          }
          
          .dh-form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
            outline: none;
          }
          
          .dh-alert {
            border: none;
            border-radius: 12px;
            padding: 1rem 1.25rem;
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }
          
          .dh-alert-success {
            background: linear-gradient(135deg, rgba(17, 153, 142, 0.1) 0%, rgba(56, 239, 125, 0.1) 100%);
            border-left: 4px solid #11998e;
            color: #047857;
          }
          
          .dh-alert-danger {
            background: linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%);
            border-left: 4px solid #ef4444;
            color: #991b1b;
          }
          
          .dh-alert-warning {
            background: linear-gradient(135deg, rgba(245, 158, 11, 0.1) 0%, rgba(217, 119, 6, 0.1) 100%);
            border-left: 4px solid #f59e0b;
            color: #92400e;
          }
        `}
      </style>

      <div className="dh-carga-bg">
        <ToastContainer />
        <div className="container-fluid py-4">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-8">
              <div className="card dh-card-main">
                <div className="card-header dh-card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <MdBed className="me-3" size={28} />
                      <h3 className="dh-header-title mb-0">
                        Carga Masiva de Camas
                      </h3>
                    </div>
                  </div>
                </div>
                <div className="card-body p-4">
                  {/* Información */}
                  <div className="alert alert-info dh-alert mb-4">
                    <strong>Instrucciones:</strong>
                    <ul className="mb-0 mt-2">
                      <li>El archivo Excel debe contener las siguientes columnas: <strong>ZONA</strong>, <strong>MODULO</strong>, <strong>HABITACION</strong>, <strong>TIPO</strong>, <strong>CAMA</strong>.</li>
                      <li><strong>ZONA:</strong> Nombre de la zona (se creará automáticamente si no existe en el proyecto).</li>
                      <li><strong>MODULO:</strong> Código o nombre del módulo (se creará automáticamente si no existe en la zona).</li>
                      <li><strong>HABITACION:</strong> Número de la habitación (se creará automáticamente si no existe en el módulo).</li>
                      <li><strong>TIPO:</strong> Tipo de habitación (INDIVIDUAL, DOBLE, TRIPLE, CUADRUPLE, CAMAROTE, etc.). La capacidad se asignará automáticamente según el tipo.</li>
                      <li><strong>CAMA:</strong> Número de la cama dentro de la habitación (1, 2, 3, etc.).</li>
                      <li><strong>Para un camarote con 2 camas:</strong> Incluya 2 filas con el mismo ZONA, MODULO, HABITACION y TIPO, pero con diferentes números de CAMA (1 y 2).</li>
                    </ul>
                  </div>

                  {/* Proyecto */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Proyecto</label>
                    <input
                      type="text"
                      className="form-control dh-form-control"
                      value={selectedProject?.nombre || "No seleccionado - ¿Inició sesión?"}
                      disabled
                    />
                  </div>

                  {/* Descargar Template */}
                  <div className="mb-4">
                    <button
                      className="btn btn-dh-info"
                      onClick={downloadTemplate}
                      disabled={isLoading}
                    >
                      <MdDownload className="me-2" />
                      Descargar Template
                    </button>
                  </div>

                  {/* Cargar Archivo */}
                  <div className="mb-4">
                    <label className="form-label fw-bold">Seleccionar Archivo Excel</label>
                    <input
                      type="file"
                      className="form-control dh-form-control"
                      accept=".xlsx,.xls"
                      onChange={handleFileChange}
                      disabled={isLoading || isValidating}
                    />
                    {isValidating && (
                      <div className="mt-2">
                        <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                          <span className="visually-hidden">Validando...</span>
                        </div>
                        <span>Validando archivo...</span>
                      </div>
                    )}
                  </div>

                  {/* Errores de Validación */}
                  {fileValidationErrors && Array.isArray(fileValidationErrors) && fileValidationErrors.length > 0 && (
                    <div className="alert alert-danger dh-alert dh-alert-danger mb-4">
                      <strong>Errores encontrados ({fileValidationErrors.length}):</strong>
                      <div className="mt-2" style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {fileValidationErrors.map((error, index) => (
                          <div key={index} className="mb-2">
                            <strong>Fila {error.fila}:</strong>
                            <ul className="mb-0">
                              {error.errores && Array.isArray(error.errores) && error.errores.map((err, errIndex) => (
                                <li key={errIndex}>{err}</li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Resultados */}
                  {results && (
                    <div className="alert alert-success dh-alert dh-alert-success mb-4">
                      <h5>Resultados del Procesamiento</h5>
                      <p className="mb-2">
                        <strong>Camas Creadas:</strong> {results.camas_creadas}
                      </p>
                      <p className="mb-2">
                        <strong>Camas Ya Existentes:</strong> {results.camas_ya_existentes || 0}
                      </p>
                      <p className="mb-2">
                        <strong>Camas Fallidas:</strong> {results.camas_fallidas}
                      </p>
                      <p className="mb-2">
                        <strong>Habitaciones Creadas:</strong> {results.habitaciones_creadas}
                      </p>
                      <p className="mb-2">
                        <strong>Habitaciones Existentes:</strong> {results.habitaciones_reutilizadas || 0}
                      </p>
                      <p className="mb-2">
                        <strong>Módulos Creados:</strong> {results.modulos_creados || 0}
                      </p>
                      <p className="mb-2">
                        <strong>Módulos Existentes:</strong> {results.modulos_reutilizados || 0}
                      </p>
                      <p className="mb-2">
                        <strong>Zonas Creadas:</strong> {results.zonas_creadas || 0}
                      </p>
                      <p className="mb-2">
                        <strong>Zonas Existentes:</strong> {results.zonas_reutilizadas || 0}
                      </p>
                      
                      {/* Solo mostrar detalle de errores si hay camas fallidas */}
                      {results.detalle_camas_fallidas && Array.isArray(results.detalle_camas_fallidas) && results.detalle_camas_fallidas.length > 0 && (
                        <div className="mt-3">
                          <strong>Detalle de Camas Fallidas:</strong>
                          <div style={{ maxHeight: "200px", overflowY: "auto" }} className="mt-2">
                            <table className="table table-sm">
                              <thead>
                                <tr>
                                  <th>Fila</th>
                                  <th>Error</th>
                                </tr>
                              </thead>
                              <tbody>
                                {results.detalle_camas_fallidas.map((item, index) => (
                                  <tr key={index}>
                                    <td>{item.fila}</td>
                                    <td>{item.error}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Botones de Acción */}
                  <div className="d-flex gap-3 justify-content-end">
                    <button
                      className="btn btn-dh-secondary"
                      onClick={resetForm}
                      disabled={isLoading}
                    >
                      <MdRefresh className="me-2" />
                      Reiniciar
                    </button>
                    <button
                      className="btn btn-dh-primary"
                      onClick={handleProcessUpload}
                      disabled={!isFileValid || isLoading || !file || hasBeenProcessed}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Procesando...
                        </>
                      ) : (
                        <>
                          <MdCloudUpload className="me-2" />
                          {hasBeenProcessed ? 'Archivo Ya Procesado' : 'Procesar Carga Masiva'}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}; // End Component

export default CargaMasivaCamas;
